import * as React from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    Typography,
    Grid,
    Button,
    Stack,
    Box,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Container
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
    ResponsiveContainer
} from "recharts";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircleIcon from '@mui/icons-material/Circle';
import { LanguageContext } from './LanguageContext';
import { authFetch } from "./../authProvider";
import { LocalMenu } from "./LocalMenu";
import { MuutosHistoria } from "./MuutosHistoria";
import CircularProgress from '@mui/material/CircularProgress';
import IconArrowUp from './Icons/IconArrowUp';
import IconArrowDown from './Icons/IconArrowDown';
import EngagementChart from "./Charts/EngagementChart";
import EngagementKpiCards from "./Charts/EngagementKpiCards";


// ============================================================================
// COMPONENT: QuickLinks / External tools
// ============================================================================
const QuickLinks = ({ items, handleLink }) => {
    if (!items || items.length === 0) return null;

    return (
        <Stack
            direction="row"
            spacing={2.5}
            useFlexGap
            flexWrap="wrap"
        >
            {items.map((d, i) => (
                <Card
                    key={i}
                    elevation={0}
                    sx={{
                        borderRadius: 999,
                        backgroundColor: '#fff',
                        border: '1px solid rgba(118,7,13,0.2)',
                        paddingX: 3.5,
                        paddingY: 1.75,
                        maxWidth: "270px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        boxShadow: '0 18px 45px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            borderColor: '#76070D',
                            boxShadow: '0 30px 60px rgba(118,7,13,0.22)',
                        }
                    }}
                    onClick={() => {
                        if (d.linkUrl.toString().substring(0, 6) === 'SIIRRY') {
                            handleLink(d.linkUrl);
                        } else if (d.linkUrl.indexOf("https://") > -1) {
                            window.open(d.linkUrl, '_blank', 'noopener,noreferrer');
                        } else {
                            window.location.href = d.linkUrl || "/";
                        }
                    }}
                >
                    <Typography
                        sx={{
                            color: "#76070D",
                            fontWeight: 600,
                            fontSize: '1rem',
                            textAlign: 'center'
                        }}
                    >
                        {d.button}
                    </Typography>

                    <OpenInNewIcon
                        sx={{
                            color: "#76070D",
                            fontSize: "16px",
                            opacity: 0.85,
                        }}
                    />
                </Card>
            ))}
        </Stack>
    );
}

const AnnouncementBoard = ({ items, handleLink, emptyText }) => {
    const getVariant = (title = '') => {
        const normalized = (title || '').toLowerCase();
        if (normalized.includes('error') || normalized.includes('virhe')) {
            return { marker: '×', color: '#76070D', bg: 'rgba(118,7,13,0.08)' };
        }
        if (normalized.includes('warning') || normalized.includes('varoitus')) {
            return { marker: '!', color: '#be6300', bg: 'rgba(190,99,0,0.08)' };
        }
        if (normalized.includes('info')) {
            return { marker: 'i', color: '#0e5f84', bg: 'rgba(14,95,132,0.08)' };
        }
        return { marker: '•', color: '#5a5a5a', bg: 'rgba(90,90,90,0.08)' };
    };

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 5,
                padding: { xs: 1.75, md: 2.5 },
                backgroundColor: '#ffffff',
                boxShadow: '0 32px 70px rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                height: 300,
                maxHeight: 350,
                width: "90%",
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {items && items.length > 0 ? (
                <Stack
                    spacing={1.5}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            width: 4
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(118,7,13,0.4)',
                            borderRadius: 999
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent'
                        }
                    }}
                >
                    {items.slice(0, 3).map((item, index) => {
                        const variant = getVariant(item.title);
                        return (
                            <Box
                                key={`${item.title}-${index}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr',
                                    gap: { xs: 1.4, sm: 2 },
                                    alignItems: 'flex-start',
                                    paddingBottom: index !== items.length - 1 ? 1.4 : 0,
                                    borderBottom: index !== items.length - 1 ? '1px solid #f1f1f1' : 'none',
                                    cursor: 'pointer'
                                }}
                                onClick={() => handleLink(item.title, item.content)}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        backgroundColor: variant.bg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: variant.color,
                                        flexShrink: 0,
                                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    {variant.marker}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 600, fontSize: '0.68rem' }}>
                                        {item.date}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.15, color: '#1f1f1f', fontSize: '0.9rem' }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#4a4a4a', mt: 0.45, lineHeight: 1.35, fontSize: '0.85rem' }}>
                                        {item.content}
                                    </Typography>
                                    {/*                                     {item.button && (
                                        <Button
                                            size="small"
                                            sx={{
                                                mt: 0.6,
                                                textTransform: 'none',
                                                color: '#76070D',
                                                fontWeight: 600,
                                                fontSize: '0.78rem',
                                                '&:hover': { backgroundColor: 'rgba(118,7,13,0.08)' }
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLink(item.title, item.content);
                                            }}
                                        >
                                            {item.button}
                                        </Button>
                                    )} */}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            ) : (
                <Box sx={{ py: 6, textAlign: 'center', color: '#6c6c6c' }}>
                    {emptyText}
                </Box>
            )}
        </Card>
    );
};

const PreviousActions = ({ items, handleLink }) => {
    if (!items || items.length === 0) return null;

    return (
        <Grid container spacing={2} sx={{ height: '100%', paddingRight: "25px", }}>
            {items.slice(0, 4).map((item, index) => (
                <Grid item xs={12} sm={6} key={`${item.title}-${index}`} sx={{ height: { xs: 'auto', md: '50%' } }}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 999,
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(118,7,13,0.15)',
                            boxShadow: '0 14px 30px rgba(0,0,0,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.9,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            height: '100%',
                            maxeight: 150,
                            '&:hover': {
                                transform: 'translateY(-6px)',
                                borderColor: '#76070D',
                                boxShadow: '0 26px 55px rgba(118,7,13,0.18)'
                            }
                        }}
                        onClick={() => {
                            if (item.linkUrl?.toString().substring(0, 6) === 'SIIRRY') {
                                handleLink(item.linkUrl);
                            } else {
                                window.open("?type=report&id=" + item.linkUrl + "&parent=tietopalvelu_" + item.serviceId, "_self");
                            }
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f1f1f', textAlign: 'center', fontSize: '1.05rem' }}>
                            {item.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                            <CircleIcon sx={{ fontSize: 10, color: item.subtitleColor || '#76070D' }} />
                            <Typography variant="body2" sx={{ color: '#555', fontSize: '0.95rem' }}>
                                {item.subtitle}
                            </Typography>
                        </Box>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// ============================================================================
// COMPONENT: Featured / Main content
// ============================================================================
const Featured = ({ item, handleLink }) => (
    <Box
        elevation={0}
        sx={{
            borderRadius: 6,
            padding: { xs: 3, md: 4.5 },
            position: 'relative',
            overflow: 'hidden'
        }}
    >
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                background: 'url(/images/background-cartoon-dim.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.08,
                pointerEvents: 'none'
            }}
        />
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="center"
            sx={{ position: 'relative' }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ color: '#1f1f1f', fontWeight: 700, letterSpacing: 0.5, mt: 0.5 }}>
                    {item.title}
                </Typography>
                <Typography variant="h5" sx={{ color: '#c41e3a', fontWeight: 700, letterSpacing: 0.5, mt: 0.5 }}>
                    {item.subtitle}
                </Typography>
            </Box>

            {item.button && (
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        backgroundColor: '#76070D',
                        borderRadius: 999,
                        paddingX: 5,
                        paddingY: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 18px 35px rgba(118,7,13,0.35)',
                        '&:hover': { backgroundColor: '#5b050a' }
                    }}
                    onClick={handleLink}
                >
                    {item.button}
                </Button>
            )}
        </Stack>
    </Box>
);

// ============================================================================
// MAIN COMPONENT: Home
// ============================================================================
export class VisualDashboard extends React.Component {
    static contextType = LanguageContext;

    constructor(props) {
        super(props);

        // Initialize component state
        this.state = {
            data: null,           // data from API
            loading: true,        // Loading state
            openHistory: false,   // History dialog state
            openMessage: false,   // Message dialog state
            messageTitle: '',     // Message dialog title
            messageText: '',       // Message dialog content
            engagementData: [],
            engagementKpi: null
        }

    }

    componentDidMount() {
        console.log("PCA inside component:", this.props.pca);
        this.fetchEngagementData();
        this.fetchEngagementKpi();
    }


    fetchEngagementData = () => {
        if (!this.props.pca) {
            console.error("MSAL instance (pca) is missing!");
            return;
        }

        authFetch(this.props.pca, "/api/Dashboard/engagement")
            .then(res => res.json())
            .then(data => {
                this.setState({ engagementData: data, loading: false });
            })
            .catch(err => {
                console.error("Engagement fetch failed:", err);
                this.setState({ loading: false });
            });
    };

    fetchEngagementKpi = () => {
        authFetch(this.props.pca, "api/Dashboard/engagement-kpi")
            .then(async res => {
                const text = await res.text();

                if (!res.ok) {
                    throw new Error(text);
                }

                return JSON.parse(text);
            })
            .then(data => {
                this.setState({ engagementKpi: data });
            })
            .catch(error => {
                console.error("KPI API error:", error);
            });
    };

    /**
     * Handles internal navigation commands (SIIRRY)
     * @param {string} funcStr - Navigation command string
     */
    handleSIIRRY = (funcStr) => {
        const params = (funcStr.replace("SIIRRY", "").replace("(", "").replace(")", "").split(",") || []).map(d => parseInt(d));
        if (this.props?.onSelectionChange && (params || []).length > 2) {
            this.props.onSelectionChange(params[0], params[1], params[2]);
        }
    }


    handleHistory = () => {
        this.setState({ openHistory: !this.state.openHistory })
    };


    handleOpenMessage = (title, msg) => {
        this.setState({
            openMessage: true,
            messageTitle: title || '',
            messageText: msg || ''
        })
    };

    handleCloseMessage = () => {
        this.setState({
            openMessage: false,
        })
    };

    render() {
        const { userLanguage, dictionary } = this.context;
        const { data, loading, openHistory, openMessage, messageTitle, messageText } = this.state;

        const lang = userLanguage === 'fi' ? 'fi' : 'en';
        const headerContent = (data?.header || {})[lang] || {};
        const featuredItems = ((data || {}).featured || {})[lang] || [];
        const previousOperations = ((data || {}).carousel || {})[lang] || [];

        const announcements = ((data || {}).announcements || {})[lang] || [];
        const quicklinks = ((data || {}).quicklinks || {})[lang] || [];


        return (
            <React.Fragment>
                <LocalMenu
                    title={dictionary.Home.Title}
                    onSearchClicked={(d) => console.log(d)}
                    pca={this.props.pca}
                />

                <Box sx={{
                    overflowY: "auto",
                    height: "calc(100vh - 58px)",
                    marginTop: "56px",
                    minHeight: "600px",
                    background: 'url(/images/background-cartoon-dim.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}>
                    <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Stack spacing={{ xs: 5, md: 6 }}>
                                {featuredItems.length > 0 && (
                                    <Featured item={featuredItems[0]} handleLink={this.handleHistory} />
                                )}

                                {/* MAIN GRID: Previous operations & announcements */}
                                <Grid container spacing={4} alignItems="stretch">
                                    <Grid item xs={12} md={12} lg={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                                            {headerContent?.title}
                                        </Typography>
                                        <Box sx={{ flex: 1, display: 'flex', minHeight: 350 }}>
                                            <PreviousActions items={previousOperations} handleLink={this.handleSIIRRY} />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={12} lg={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                                            {headerContent?.titleAnnouncements}
                                        </Typography>
                                        <Box sx={{ flex: 1, display: 'flex', minHeight: 350 }}>
                                            <AnnouncementBoard
                                                items={announcements}
                                                handleLink={this.handleOpenMessage}
                                                emptyText={dictionary.Karuselli.NoAnnouncements}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/*  EXTERNAL TOOLS / QUICK LINKS */}
                                <Box>
                                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                                        {headerContent?.titleLinks}
                                    </Typography>
                                    {quicklinks.length > 0 ? (
                                        <QuickLinks items={quicklinks} handleLink={this.handleSIIRRY} />
                                    ) : (
                                        <Paper elevation={0} variant="outlined">
                                            <Box sx={{ p: 4, textAlign: 'center', color: '#646464' }}>
                                            </Box>
                                        </Paper>
                                    )}
                                </Box>

                                {/* ENGAGEMENT CHART */}
                                <EngagementKpiCards data={this.state.engagementKpi} />

                                <EngagementChart data={this.state.engagementData} />
                            </Stack>
                        )}
                    </Container>
                </Box>

                <Dialog
                    open={openHistory}
                    onClose={this.handleHistory}
                >
                    <DialogTitle>
                        {dictionary.Karuselli.HistoryTitle}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ color: "#646464" }}>
                            <MuutosHistoria />
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" color="primary" style={{ color: "#fff" }} onClick={this.handleHistory}>
                            {dictionary.Close}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openMessage}
                    onClose={this.handleCloseMessage}
                >
                    <DialogTitle>
                        {messageTitle}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ color: "#646464" }}>
                            <p>{messageText}</p>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" color="primary" style={{ color: "#fff" }} onClick={this.handleCloseMessage}>
                            {dictionary.Close}
                        </Button>
                    </DialogActions>
                </Dialog>

            </React.Fragment>
        );
    }
}

